// "Add a file": a button that opens the system file picker, then a form in place asking who
// can see the file. Nothing is chosen at first, so nobody shares a will with everyone by
// pressing Add without reading. Also holds the radio group the menu's "Change who can see
// it" reuses.

import { useId, useRef, useState, type ReactNode } from 'react';

import { ApiFailure, type ApiClient } from '../api/client.js';
import type { Person } from '../api/contract.js';
import { nameList } from '../format.js';

export type Visibility = 'just_me' | 'principals' | 'everyone';

const firstName = (name: string): string => name.split(' ')[0] ?? name;

const capitalised = (text: string): string =>
  `${text.charAt(0).toUpperCase()}${text.slice(1)}`;

function sizeLabel(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function fileProblem(error: unknown): string {
  if (error instanceof ApiFailure && error.code === 'file_too_large') {
    return 'That file is too large. Files can be up to 25 MB.';
  }
  if (error instanceof ApiFailure && error.code === 'files_unavailable') {
    return 'Files can’t be added just now. Try again later.';
  }
  return 'That didn’t go through. Try again.';
}

type Option = {
  readonly value: Visibility;
  readonly label: string;
  readonly help: string;
};

function optionsFor(people: readonly Person[], viewerIsPrincipal: boolean): Option[] {
  const others = people.filter((person) => !person.self);
  const otherPrincipals = others.filter((person) => person.sameSay);
  const names = (list: readonly Person[]): string[] =>
    list.map((person) => firstName(person.name));
  const options: Option[] = [
    {
      value: 'just_me',
      label: 'Just me',
      help: 'Used in your conversations, and shown to no one else.',
    },
  ];
  if (viewerIsPrincipal) {
    options.push({
      value: 'principals',
      label: 'All principals',
      help:
        otherPrincipals.length === 0
          ? 'Just you.'
          : `${capitalised(nameList(['you', ...names(otherPrincipals)]))}.`,
    });
  }
  options.push({
    value: 'everyone',
    label: 'Everyone in the plan',
    help:
      others.length === 0
        ? 'Just you, and anyone invited later.'
        : `${capitalised(nameList(['you', ...names(others)]))}, and anyone invited later.`,
  });
  return options;
}

/** The viewer's choices as a radio group. Used by the add form and by "Change who can see it". */
export function VisibilityPicker({
  people,
  viewerIsPrincipal,
  value,
  onChange,
}: {
  people: readonly Person[];
  viewerIsPrincipal: boolean;
  value: Visibility | null;
  onChange: (value: Visibility) => void;
}): JSX.Element {
  const group = useId();
  return (
    <fieldset className="fileChoice">
      <legend className="fileChoice__legend">Who can see it?</legend>
      {optionsFor(people, viewerIsPrincipal).map((option) => (
        <label key={option.value} className="fileChoice__option">
          <input
            type="radio"
            className="radio"
            name={group}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <span className="fileChoice__text">
            <span className="fileChoice__label">{option.label}</span>
            <span className="fileChoice__help">{option.help}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

export type AddFileProps = {
  client: ApiClient;
  planId: string;
  people: readonly Person[];
  viewerIsPrincipal: boolean;
  originThreadId: string | null;
  note?: ReactNode;
  /** Shows the trigger as a dashed "Add" row, like the one under a topic's questions. */
  asRow?: boolean;
  /** Shows the trigger as a small text link instead of a button. */
  asLink?: boolean;
  onAdded: () => void;
};

export function AddFile({
  client,
  planId,
  people,
  viewerIsPrincipal,
  originThreadId,
  note,
  asRow = false,
  asLink = false,
  onAdded,
}: AddFileProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [visibility, setVisibility] = useState<Visibility | null>('just_me');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const close = (): void => {
    setFile(null);
    setVisibility('just_me');
    setProblem(null);
  };

  const add = async (): Promise<void> => {
    if (file === null || visibility === null) {
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      await client.uploadFile(planId, file, visibility, originThreadId);
      close();
      onAdded();
    } catch (error) {
      setProblem(fileProblem(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={asRow ? 'addFile addFile--row' : 'addFile'}>
      <input
        ref={inputRef}
        type="file"
        className="addFile__input"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          const chosen = event.target.files?.[0] ?? null;
          event.target.value = '';
          if (chosen !== null) {
            setFile(chosen);
            setVisibility('just_me');
            setProblem(null);
          }
        }}
      />
      {asLink ? (
        <button
          type="button"
          className="discoveryLink addFile__link"
          disabled={file !== null}
          onClick={() => inputRef.current?.click()}
        >
          Add a file →
        </button>
      ) : asRow ? (
        <button
          type="button"
          className="addPerson"
          disabled={file !== null}
          onClick={() => inputRef.current?.click()}
        >
          <span className="addPerson__plus" aria-hidden="true">
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M7 2v10" />
              <path d="M2 7h10" />
            </svg>
          </span>
          Add a file
        </button>
      ) : (
        <button
          type="button"
          className="button"
          disabled={file !== null}
          onClick={() => inputRef.current?.click()}
        >
          Add a file
        </button>
      )}
      {file === null ? null : (
        <form
          className="addFile__form"
          onSubmit={(event) => {
            event.preventDefault();
            void add();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && !busy) {
              close();
            }
          }}
        >
          <p className="addFile__file">
            <span className="addFile__name">{file.name}</span>
            <span className="addFile__size">{sizeLabel(file.size)}</span>
          </p>
          <VisibilityPicker
            people={people}
            viewerIsPrincipal={viewerIsPrincipal}
            value={visibility}
            onChange={setVisibility}
          />
          {note === undefined ? null : <p className="addFile__note">{note}</p>}
          {problem === null ? null : (
            <p className="artifact__problem" role="alert">
              {problem}
            </p>
          )}
          <div className="fileForm__buttons">
            <button type="button" className="button" disabled={busy} onClick={close}>
              Cancel
            </button>
            <button
              type="submit"
              className="button button--primary"
              disabled={busy || visibility === null}
            >
              Add
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
